import { RoleFunction } from '@eagle/common';
import { DashboardType } from '@eagle/core-data-types';
import {
  APP_BANNER_HEIGHT,
  APP_BAR_HEIGHT,
  AuthStatus,
  DashboardAppBarProps,
  dashboardAppBarStyles,
  DashboardSwitcher,
  DashboardSwitcherLocal,
  ErrorPage,
  Icon,
  MicroPortalLauncher,
  MiddleSpinner,
  NotFound,
  PageTitle,
  SIDEBAR_FULL_WIDTH,
  SIDEBAR_WIDTH,
  SisenseComposeDashboard,
  SisenseDashboard,
  Undefinable,
  useAuth,
  useAuthenticated,
  useBoolFlag,
  useCustomRoutes,
  useHasAuthorization,
  usePromise,
  useSidebarPinContext,
  useTitle
} from '@eagle/react-common';
import { AppBar, Box, Breadcrumbs, Button, Divider, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { Link, Toolbar, useTheme } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref, useParams } from 'react-router-dom';
import { SPARE_FLEET_CAPACITY_FLAG } from '../reports-spare-fleet-capacity/reports-spare-fleet-capacity';

const USER_ROLE = [RoleFunction.USER] as const;
const DATA_VIEWER_ROLE = [RoleFunction.DATA_VIEWER] as const;
const SYSTEM_ADMINISTRATOR_ROLE = [RoleFunction.SYSTEM_ADMINISTRATOR] as const;

const DashboardAppBar = styled(AppBar)<DashboardAppBarProps>(dashboardAppBarStyles);

const OuterStack = styled(Stack)({
  height: '100%',
  width: '100%',
  '.showAppBanner & .MuiAppBar-root': { marginTop: `${APP_BANNER_HEIGHT}px` },
});

const MainStack = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const PageTitleBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const DashboardBox = styled(Box, { shouldForwardProp: (prop) => prop !== 'applyIframeMargin' })<{
  applyIframeMargin: boolean;
}>(({ applyIframeMargin }) => ({
  ...(applyIframeMargin
    ? {
      '& iframe': {
        marginTop: '14px',
      },
    }
    : undefined),
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

export const ReportsPage: FC = () => {
  const { t } = useTranslation(['track', 'common']);
  const { isPinned } = useSidebarPinContext();
  const { restClient } = useAuthenticated();
  const { hasAuthorization } = useHasAuthorization();
  const { state } = useAuth();
  const { dashboardId } = useParams();
  const { reports } = useCustomRoutes();
  const [dashboardName, setDashboardName] = useState<Undefinable<string>>();
  const v2DashSwitching = useBoolFlag('portals-dashboard-switching-enhancements-temporary-20240918') ?? false;
  const schedulerFeature = useBoolFlag('track-report-scheduler-feature') ?? false;
  const sisenseComposeSdkEnabled = useBoolFlag('portals-sisense-compose-sdk-feature') ?? false;
  const spareFleetCapacityFlagEnabled = useBoolFlag(SPARE_FLEET_CAPACITY_FLAG) ?? false;
  const hasSystemAdminPermissions = hasAuthorization(SYSTEM_ADMINISTRATOR_ROLE);
  const showSpareFleetCapacity = hasAuthorization(USER_ROLE) && hasAuthorization(DATA_VIEWER_ROLE) && spareFleetCapacityFlagEnabled;
  const switcherStaticItems = useMemo(() => showSpareFleetCapacity
    ? [{ id: 'spare-fleet-capacity', label: t('track:page.reports.spare-fleet-capacity.title'), path: `/${reports}/spare-fleet-capacity` }]
    : [], [reports, showSpareFleetCapacity, t]);
  const renderTitle = useCallback(() => {
    return (
      <DashboardSwitcher dashboardId={dashboardId} staticItems={switcherStaticItems} type={DashboardType.REPORT} sx={{ flex: 1, justifyItems: 'right', mr: -0.5 }}>
        <Typography noWrap variant="body1" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" mr={1} ml="auto">
          {dashboardName}
        </Typography>
      </DashboardSwitcher>
    );
  }, [dashboardName, dashboardId, switcherStaticItems]);
  useTitle(renderTitle(), true);
  const theme = useTheme();
  const href = useHref('/reports');
  const manageHref = useHref(dashboardId ? `/reports/manage/${dashboardId}` : '');

  const [dashboard, dashboardError, dashboardState] = usePromise(async () => {
    const filter = { '_id': dashboardId };
    const result = await restClient.my.dashboards.getAllV2({
      filter,
    });
    return result?.items.length ? result?.items[0] : undefined;
  }, [dashboardId, restClient]);

  useEffect(() => {
    if (!dashboard) return;
    setDashboardName(dashboard.display);
  }, [dashboard]);

  if (dashboardState === 'pending' || (!state || state.status !== AuthStatus.AUTHENTICATED)) return <MiddleSpinner />;
  if (dashboardError) return <ErrorPage error={dashboardError} />;
  if (!dashboard) return <NotFound />;

  const { account, user } = state;

  if (v2DashSwitching) {
    return (
      <OuterStack>
        <MainStack
          alignItems="center"
          direction="row"
        >
          <Stack spacing={1} flex={1} >
            <PageTitleBox>
              <PageTitle title={dashboardName} icon={<Icon icon="ReportsIcon" />} />
            </PageTitleBox>
            <Box>
              <DashboardSwitcherLocal type={DashboardType.REPORT} dashboardId={dashboardId} staticItems={switcherStaticItems}>
                <Typography variant="body2" ml={5.2}>{t('common:component.navigation.switch-to.labels')}</Typography>
              </DashboardSwitcherLocal>
            </Box>
          </Stack>
          {hasSystemAdminPermissions && manageHref
            && <Box>
              <Button href={manageHref} >
                {t('track:page.dashboards.manage.action')}
              </Button>
            </Box>
          }
          {dashboard.scheduling && schedulerFeature
            && <Box ml={2}>
              <MicroPortalLauncher app='scheduler' scrolling='yes' data={{ dashboard: dashboard._id, user: user._id, accountId: account._id }} type='dialog' loadingElement={<Button disabled startIcon={<Icon icon='CalendarIcon' />}>{t('common:common.labels.loading')}</Button>} element={<Button startIcon={<Icon icon='CalendarIcon' />}>{t('track:page.reports.scheduler.label')}</Button>} />
            </Box>
          }
        </MainStack>
        <Divider />
        <DashboardBox applyIframeMargin={!v2DashSwitching}>
          {sisenseComposeSdkEnabled ?
            <SisenseComposeDashboard type='reports' dashboardId={dashboardId ?? 'error'} />
            :
            <SisenseDashboard type='reports' dashboardId={dashboardId ?? 'error'} />
          }
        </DashboardBox>
      </OuterStack>
    );
  }

  return (
    <OuterStack>
      <MainStack>
        <DashboardAppBar
          height={APP_BAR_HEIGHT}
          isPinned={isPinned}
          sidebarFullWidth={SIDEBAR_FULL_WIDTH}
          sidebarWidth={SIDEBAR_WIDTH}
        >
          <Toolbar sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', width: 1, '& > :last-child': { ml: 'auto', flexShrink: 0 } }}>
            <Breadcrumbs color={theme.palette.common.white}>
              <Link color={theme.palette.common.white} href={href}>
                {t('track:page.reports.title')}
              </Link>
              <DashboardSwitcher dashboardId={dashboardId} staticItems={switcherStaticItems} type={DashboardType.REPORT}>
                <Typography>{dashboardName}</Typography>
              </DashboardSwitcher>
            </Breadcrumbs>
            <Stack direction="row" spacing={1}>
              {hasSystemAdminPermissions && manageHref
                && <Box>
                  <Button variant='contained' href={manageHref} >
                    {t('track:page.dashboards.manage.action')}
                  </Button>
                </Box>
              }
              {dashboard.scheduling && schedulerFeature && (
                <MicroPortalLauncher
                  app='scheduler'
                  scrolling='yes'
                  data={{ dashboard: dashboard._id, user: user._id, accountId: account._id }}
                  type='dialog'
                  loadingElement={
                    <Button disabled startIcon={<Icon icon='CalendarIcon' />}>
                      {t('common:common.labels.loading')}
                    </Button>}
                  element={
                    <Button variant='contained' startIcon={<Icon icon='CalendarIcon' />}>
                      {t('track:page.reports.scheduler.label')}
                    </Button>}
                />
              )}
            </Stack>
          </Toolbar>
        </DashboardAppBar>
      </MainStack>
      <DashboardBox applyIframeMargin={!v2DashSwitching}>
        {sisenseComposeSdkEnabled ?
          <SisenseComposeDashboard type='reports' dashboardId={dashboardId ?? 'error'} />
          :
          <SisenseDashboard type='reports' dashboardId={dashboardId ?? 'error'} />
        }
      </DashboardBox>
    </OuterStack>
  );
};
